// src/providers/theme-provider.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MantineProvider, createTheme, MantineColorScheme, MantineColorsTuple } from '@mantine/core';

interface ThemeContextType {
  colorScheme: MantineColorScheme;
  toggleColorScheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Provider leggero che fornisce solo il context del tema senza wrappare MantineProvider.
 * Usare questo quando MantineProvider è già presente nel tree.
 */
export function ThemeContextProvider({ children }: { children: ReactNode }) {
  const [colorScheme, setColorScheme] = useState<MantineColorScheme>('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as MantineColorScheme | null;
    if (savedTheme) {
      setColorScheme(savedTheme);
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setColorScheme('dark');
    }
  }, []);

  const toggleColorScheme = () => {
    const newColorScheme = colorScheme === 'dark' ? 'light' : 'dark';
    setColorScheme(newColorScheme);
    localStorage.setItem('theme', newColorScheme);
  };

  return (
    <ThemeContext.Provider value={{ colorScheme, toggleColorScheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [colorScheme, setColorScheme] = useState<MantineColorScheme>('light');

  // Al caricamento, leggi preferenza tema dal localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as MantineColorScheme | null;
    if (savedTheme) {
      setColorScheme(savedTheme);
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setColorScheme('dark');
    }
  }, []);

  // Toggle tema e salva preferenza
  const toggleColorScheme = () => {
    const newColorScheme = colorScheme === 'dark' ? 'light' : 'dark';
    setColorScheme(newColorScheme);
    localStorage.setItem('theme', newColorScheme);
  };

  // Palette colori Ginori elegante con maggiore contrasto
  const ginoriGold: MantineColorsTuple = [
    '#faf9f6',
    '#f5f2ec',
    '#ede5d4',
    '#e4d8bb',
    '#dccda7',
    '#d4c299',
    '#c9b585',
    '#b5a074',
    '#9a8560',
    '#7a6b4c'
  ];

  const ginoriBlue: MantineColorsTuple = [
    '#f4f6f8',
    '#e8ecf0',
    '#d1d9e1',
    '#b8c5d1',
    '#9fb0c2',
    '#869db6',
    '#6d8aa9',
    '#54779c',
    '#3b648f',
    '#2d4d73'
  ];

  const ginoriNeutral: MantineColorsTuple = [
    '#fdfcfa',
    '#f8f7f4',
    '#f0ede8',
    '#e7e2db',
    '#ded8cf',
    '#d6cfc5',
    '#ccc4b8',
    '#b5ada1',
    '#9c9488',
    '#6b6459'
  ];

  // Crea il tema Mantine elegante e raffinato
  const theme = createTheme({
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    fontFamilyMonospace: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace',
    headings: { 
      fontFamily: '"Playfair Display", "Times New Roman", serif',
      fontWeight: '300',
      sizes: {
        h1: { fontSize: '3rem', lineHeight: '1.2' },
        h2: { fontSize: '2.25rem', lineHeight: '1.3' },
        h3: { fontSize: '1.75rem', lineHeight: '1.4' },
        h4: { fontSize: '1.5rem', lineHeight: '1.4' },
        h5: { fontSize: '1.25rem', lineHeight: '1.5' },
        h6: { fontSize: '1.125rem', lineHeight: '1.5' },
      }
    },
    defaultRadius: 'xs',
    spacing: {
      xs: '0.75rem',
      sm: '1rem',
      md: '1.5rem',
      lg: '2rem',
      xl: '3rem',
    },
    shadows: {
      xs: '0 1px 3px rgba(0, 0, 0, 0.08)',
      sm: '0 1px 4px rgba(0, 0, 0, 0.1)',
      md: '0 4px 12px rgba(0, 0, 0, 0.08)',
      lg: '0 8px 24px rgba(0, 0, 0, 0.12)',
      xl: '0 12px 32px rgba(0, 0, 0, 0.15)',
    },
    primaryColor: 'ginoriGold',
    colors: {
      ginoriGold,
      ginoriBlue,
      ginoriNeutral,
    },
    components: {
      AppShell: {
        styles: {
          main: {
            backgroundColor: 'var(--mantine-color-ginoriNeutral-0)',
            minHeight: '100vh',
            fontFamily: '"Inter", sans-serif',
          },
          header: {
            backgroundColor: 'var(--mantine-color-white)',
            borderBottom: '1px solid var(--mantine-color-ginoriNeutral-2)',
            backdropFilter: 'blur(12px)',
          },
          navbar: {
            backgroundColor: 'var(--mantine-color-white)',
            borderRight: '1px solid var(--mantine-color-ginoriNeutral-2)',
            width: '200px',
          },
        },
      },
      Container: {
        styles: {
          root: {
            paddingLeft: '2rem',
            paddingRight: '2rem',
          },
        },
      },
      Button: {
        defaultProps: {
          size: 'md',
          radius: 'xs',
        },
        styles: {
          root: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            letterSpacing: '0.025em',
            textTransform: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      Paper: {
        styles: {
          root: {
            backgroundColor: 'var(--mantine-color-white)',
            border: '1px solid var(--mantine-color-ginoriNeutral-2)',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'var(--mantine-color-ginoriGold-3)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            },
          },
        },
      },
      Text: {
        styles: {
          root: {
            fontFamily: '"Inter", sans-serif',
            lineHeight: 1.6,
            letterSpacing: '0.01em',
            color: 'var(--mantine-color-ginoriBlue-9)',
          },
        },
      },
      Title: {
        styles: {
          root: {
            fontFamily: '"Playfair Display", serif',
            fontWeight: 300,
            letterSpacing: '-0.01em',
            color: 'var(--mantine-color-ginoriBlue-9)',
          },
        },
      },
      UnstyledButton: {
        styles: {
          root: {
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'var(--mantine-color-ginoriNeutral-1)',
              transform: 'translateX(2px)',
            },
          },
        },
      },
      Table: {
        styles: {
          root: {
            borderCollapse: 'separate',
            borderSpacing: 0,
            backgroundColor: 'var(--mantine-color-white)',
            borderRadius: '4px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          },
          th: {
            backgroundColor: 'var(--mantine-color-ginoriNeutral-1)',
            fontFamily: '"Playfair Display", serif',
            fontWeight: 400,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            color: 'var(--mantine-color-ginoriBlue-9)',
            borderBottom: '1px solid var(--mantine-color-ginoriNeutral-3)',
            padding: '1rem',
          },
          td: {
            fontFamily: '"Inter", sans-serif',
            borderBottom: '1px solid var(--mantine-color-ginoriNeutral-2)',
            padding: '1rem',
            fontSize: '0.875rem',
            color: 'var(--mantine-color-ginoriBlue-9)',
          },
          tr: {
            '&:hover': {
              backgroundColor: 'var(--mantine-color-ginoriNeutral-0)',
            },
          },
        },
      },
      Badge: {
        styles: {
          root: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            borderRadius: '2px',
          },
        },
      },
      Card: {
        styles: {
          root: {
            backgroundColor: 'var(--mantine-color-white)',
            border: '1px solid var(--mantine-color-ginoriNeutral-2)',
            borderRadius: '4px',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
              borderColor: 'var(--mantine-color-ginoriGold-3)',
            },
          },
        },
      },
      Input: {
        styles: {
          input: {
            fontFamily: '"Inter", sans-serif',
            border: '1px solid var(--mantine-color-ginoriNeutral-3)',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            '&:focus': {
              borderColor: 'var(--mantine-color-ginoriGold-5)',
              boxShadow: '0 0 0 2px rgba(212, 194, 153, 0.2)',
            },
          },
        },
      },
      Divider: {
        styles: {
          root: {
            borderColor: 'var(--mantine-color-ginoriNeutral-2)',
          },
        },
      },
      Menu: {
        styles: {
          dropdown: {
            backgroundColor: 'var(--mantine-color-white)',
            border: '1px solid var(--mantine-color-ginoriNeutral-2)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            borderRadius: '4px',
            backdropFilter: 'blur(12px)',
          },
          item: {
            fontFamily: '"Inter", sans-serif',
            fontSize: '0.875rem',
            fontWeight: 400,
            padding: '12px 16px',
            color: 'var(--mantine-color-ginoriBlue-8)',
            '&:hover': {
              backgroundColor: 'var(--mantine-color-ginoriNeutral-1)',
            },
          },
        },
      },
      Tabs: {
        styles: {
          root: {
            borderBottom: '1px solid var(--mantine-color-ginoriNeutral-2)',
          },
          tab: {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            letterSpacing: '0.3px',
            color: 'var(--mantine-color-ginoriBlue-6)',
            '&:hover': {
              backgroundColor: 'var(--mantine-color-ginoriNeutral-1)',
            },
            '&[data-active="true"]': {
              color: 'var(--mantine-color-ginoriGold-8)',
              borderColor: 'var(--mantine-color-ginoriGold-6)',
            },
          },
        },
      },
      Modal: {
        styles: {
          content: {
            backgroundColor: 'var(--mantine-color-white)',
            borderRadius: '4px',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          },
          header: {
            backgroundColor: 'var(--mantine-color-ginoriNeutral-0)',
            borderBottom: '1px solid var(--mantine-color-ginoriNeutral-2)',
            padding: '1.5rem',
          },
          body: {
            padding: '1.5rem',
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ colorScheme, toggleColorScheme }}>
      <MantineProvider theme={theme} defaultColorScheme={colorScheme}>
        {children}
      </MantineProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}